// ── Standard QueryClient defaults (ASC-6341) ─────────────────────────────────
// Every agent (and the platform host) instantiates its own `QueryClient` —
// MFEs are separately bundled, so there is no cross-boundary singleton to
// share. What WAS inconsistent across `QueryProvider.tsx` copies (no options
// at all / retry: 1 / default retry: 3, staleTime 30s / 5min) is now one
// factory every copy calls, so "standard QueryClient" means the same thing
// everywhere.
//
// Deliberately untyped against `@tanstack/react-query`'s `DefaultOptions` —
// `agent-sdk` has no dependency on that package (every consumer already
// depends on `@tanstack/react-query` directly), and this return shape is
// structurally compatible with `QueryClientConfig['defaultOptions']`.

const DEFAULT_STALE_TIME_MS = 60_000
const DEFAULT_RETRY_COUNT = 1

export interface StandardQueryClientDefaults {
  queries: {
    retry: number
    staleTime: number
    refetchOnWindowFocus: boolean
  }
}

export interface QueryClientDefaultsOverrides {
  /** Per-agent staleTime override, in ms — defaults to 60s. */
  staleTime?: number
}

/**
 * Standard `defaultOptions` for `new QueryClient({ defaultOptions: ... })`.
 * Pass the result straight through — every `QueryProvider.tsx` in the repo
 * should call this instead of hand-rolling its own options object.
 */
export function createQueryClientDefaults(
  overrides?: QueryClientDefaultsOverrides,
): StandardQueryClientDefaults {
  return {
    queries: {
      retry: DEFAULT_RETRY_COUNT,
      staleTime: overrides?.staleTime ?? DEFAULT_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    },
  }
}
